import { useState } from 'react';
import type { FormEvent } from 'react';
import { Modal } from '../../components/ui/Modal';
import { Field, ColorPicker } from '../../components/ui/Field';
import { useFormErrors } from '../../lib/useFormErrors';
import { siteMutations } from '../../lib/queries';
import { useToast } from '../../components/ui/Toast';
import { SITE_STATUS_LABELS, PALETTE } from '../../lib/labels';
import type { Site, SiteStatus } from '../../lib/types';

type FormState = Omit<Site, 'id'>;

const BLANK: FormState = {
  name: '',
  shortCode: '',
  color: PALETTE[3],
  address: '',
  client: '',
  startDate: null,
  endDate: null,
  status: 'aktiv',
  notes: '',
  sortOrder: 0,
  permanent: false,
};

export function SiteDialog({ site, onClose }: { site: Site | null; onClose: () => void }) {
  const isEdit = site !== null;
  const [form, setForm] = useState<FormState>(site ? toFormState(site) : BLANK);
  const { errors, capture, clear, fieldError } = useFormErrors();
  const toast = useToast();

  const create = siteMutations.useCreate();
  const update = siteMutations.useUpdate();
  const busy = create.isPending || update.isPending;

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((current) => ({ ...current, [key]: value }));

  const submit = (event: FormEvent) => {
    event.preventDefault();
    clear();
    const onSuccess = () => {
      toast.success(isEdit ? 'Baustelle gespeichert.' : `Baustelle "${form.name}" angelegt.`);
      onClose();
    };
    if (isEdit) {
      update.mutate({ ...form, id: site.id }, { onSuccess, onError: capture });
    } else {
      create.mutate(form, { onSuccess, onError: capture });
    }
  };

  return (
    <Modal
      title={isEdit ? 'Baustelle bearbeiten' : 'Neue Baustelle'}
      subtitle={isEdit ? site.name : 'Ziel für die Einteilung im Schichtplan'}
      onClose={onClose}
      width="voll"
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={busy}>
            Abbrechen
          </button>
          <button type="submit" form="site-form" className="btn btn-signal" disabled={busy}>
            {busy ? 'Wird gespeichert …' : 'Speichern'}
          </button>
        </>
      }
    >
      <form id="site-form" className="stack" onSubmit={submit} noValidate>
        {errors.general ? (
          <p className="form-error" role="alert">
            {errors.general}
          </p>
        ) : null}

        <div className="form-grid">
          <Field label="Bezeichnung" error={fieldError('name')} className="span-2">
            {(props) => (
              <input
                {...props}
                className="input"
                autoFocus
                placeholder="z. B. Neubau Schulstraße"
                value={form.name}
                onChange={(event) => set('name', event.target.value)}
              />
            )}
          </Field>

          <Field label="Status" error={fieldError('status')}>
            {(props) => (
              <select
                {...props}
                className="select"
                value={form.status}
                onChange={(event) => set('status', event.target.value as SiteStatus)}
              >
                {Object.entries(SITE_STATUS_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            )}
          </Field>

          <Field label="Auftraggeber" error={fieldError('client')}>
            {(props) => (
              <input
                {...props}
                className="input"
                value={form.client}
                onChange={(event) => set('client', event.target.value)}
              />
            )}
          </Field>

          <Field label="Adresse" error={fieldError('address')}>
            {(props) => (
              <input
                {...props}
                className="input"
                value={form.address}
                onChange={(event) => set('address', event.target.value)}
              />
            )}
          </Field>

          <Field label="Baubeginn" error={fieldError('startDate')}>
            {(props) => (
              <input
                {...props}
                className="input"
                type="date"
                value={form.startDate ?? ''}
                onChange={(event) => set('startDate', event.target.value || null)}
              />
            )}
          </Field>

          <Field label="Bauende" error={fieldError('endDate')}>
            {(props) => (
              <input
                {...props}
                className="input"
                type="date"
                value={form.endDate ?? ''}
                onChange={(event) => set('endDate', event.target.value || null)}
              />
            )}
          </Field>

          <Field label="Reihenfolge" error={fieldError('sortOrder')} hint="Kleinere Zahl zuerst">
            {(props) => (
              <input
                {...props}
                className="input"
                type="number"
                min={0}
                value={form.sortOrder}
                onChange={(event) => set('sortOrder', Number(event.target.value))}
              />
            )}
          </Field>

          <div className="span-2">
            <ColorPicker label="Farbe im Plan" value={form.color} onChange={(color) => set('color', color)} />
          </div>

          <div className="span-2">
            <label className="check">
              <input
                type="checkbox"
                checked={form.permanent}
                onChange={(event) => set('permanent', event.target.checked)}
              />
              Fester Bereich — steht jeden Tag (Mo–So) im Plan
            </label>
            <p className="muted" style={{ margin: '0.25rem 0 0' }}>
              Für Störungsdienst, Lager oder Büro: erscheint automatisch an jedem Tag als
              hervorgehobene Zeile und muss nie eingeplant werden.
            </p>
          </div>

          <Field label="Notiz" error={fieldError('notes')} className="span-3">
            {(props) => (
              <textarea
                {...props}
                className="textarea"
                value={form.notes}
                onChange={(event) => set('notes', event.target.value)}
              />
            )}
          </Field>
        </div>
      </form>
    </Modal>
  );
}

function toFormState(site: Site): FormState {
  const { id: _id, ...rest } = site;
  return rest;
}
